/**
 * PHIA picker + inline tag (CD R10 Q1 verdict: "PHIA tags inline + hover").
 *
 * The picker is the ONLY way an operator can author a PHIA-tagged
 * assertion in the Minister Briefing Pack. The Pack REFUSES to publish
 * an assertion without both a PHIA word and an analytical-confidence
 * rating (per minister-briefing research subagent's recommendation +
 * CD R10 Q2 verdict on the picker being frictionless-but-visible).
 */

import { useState } from 'react';
import {
  PHIA_BANDS, type AnalyticalConfidence, type PhiaAssertion,
} from './types';

/** Inline PHIA tag — the rendered form in the pack. Hover shows the
 *  % range + analytical confidence. Underline-dotted accent visually
 *  signals "this is a calibrated claim, not loose prose." */
export function PhiaTag({ assertion }: { assertion: PhiaAssertion }) {
  const band = PHIA_BANDS.find(b => b.word === assertion.word);
  return (
    <span
      tabIndex={0}
      style={{
        color: 'var(--bm-accent, #6ea3ff)',
        borderBottom: '1px dotted var(--bm-accent, #6ea3ff)',
        cursor: 'help',
        position: 'relative' as const,
        fontWeight: 600,
      }}
      title={band ? `${band.range} · analytical confidence: ${assertion.confidence}` : ''}
    >
      {assertion.word}
    </span>
  );
}

/** Picker — operator selects PHIA word + confidence. Used in the Pack
 *  builder. */
export function PhiaPicker({
  value, onChange, compact = false,
}: {
  value: PhiaAssertion | null;
  onChange: (next: PhiaAssertion) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (compact && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          padding: '3px 8px',
          fontFamily: 'var(--bm-font-mono)', fontSize: 11,
          color: value ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-text-subtle)',
          background: 'transparent',
          border: '1px dashed var(--bm-border)',
          borderRadius: 4, cursor: 'pointer',
        }}
      >
        {value ? `${value.word} · ${value.confidence}` : '+ PHIA tag'}
      </button>
    );
  }
  return (
    <div style={{
      display: 'inline-grid', gap: 6,
      padding: 10,
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 6,
    }}>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: '.6px', textTransform: 'uppercase',
        color: 'var(--bm-text-subtle)',
      }}>PHIA likelihood</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {PHIA_BANDS.map(b => (
          <button
            key={b.word}
            type="button"
            onClick={() => onChange({ word: b.word, confidence: value?.confidence ?? 'Moderate' })}
            style={{
              padding: '3px 8px',
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
              color: value?.word === b.word ? '#fff' : 'var(--bm-text-muted)',
              background: value?.word === b.word ? 'var(--bm-accent, #6ea3ff)' : 'transparent',
              border: '1px solid var(--bm-border)', borderRadius: 4, cursor: 'pointer',
            }}
            title={b.range}
          >{b.word}</button>
        ))}
      </div>
      <div style={{
        marginTop: 4,
        fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: '.6px', textTransform: 'uppercase',
        color: 'var(--bm-text-subtle)',
      }}>Analytical confidence</div>
      <div style={{ display: 'flex', gap: 4 }}>
        {(['Low', 'Moderate', 'High'] as readonly AnalyticalConfidence[]).map(c => (
          <button
            key={c}
            type="button"
            onClick={() => onChange({ word: value?.word ?? 'realistic possibility', confidence: c })}
            style={{
              padding: '3px 9px',
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
              color: value?.confidence === c ? '#fff' : 'var(--bm-text-muted)',
              background: value?.confidence === c ? 'var(--bm-accent, #6ea3ff)' : 'transparent',
              border: '1px solid var(--bm-border)', borderRadius: 4, cursor: 'pointer',
            }}
          >{c}</button>
        ))}
      </div>
      {compact && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            marginTop: 4,
            padding: '3px 8px',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10,
            color: 'var(--bm-text-subtle)',
            background: 'transparent', border: 'none', cursor: 'pointer',
          }}
        >done</button>
      )}
    </div>
  );
}

/** Inline assertion-with-picker — operator clicks to reveal picker. */
export function PhiaAssertionInline({
  assertion, onChange,
}: { assertion: PhiaAssertion | null; onChange: (next: PhiaAssertion) => void }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return <PhiaPicker value={assertion} onChange={(a) => { onChange(a); setEditing(false); }} compact />;
  }
  if (!assertion) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        style={{
          padding: '1px 7px',
          fontFamily: 'var(--bm-font-mono)', fontSize: 11,
          color: 'var(--bm-text-subtle)', background: 'transparent',
          border: '1px dashed var(--bm-text-subtle)', borderRadius: 4, cursor: 'pointer',
        }}
      >+ PHIA</button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      <PhiaTag assertion={assertion} />
    </button>
  );
}
